import React, {
  CSSProperties,
  ComponentType,
  MouseEventHandler,
  ReactNode,
  forwardRef,
} from 'react';
import styled, { css } from 'styled-components';
import { ColorProps, MarginProps, space } from 'styled-system';

import { ChevronUpIcon } from 'components/ChevronIcon';
import Container from 'components/Container';
import {
  NavLink as VersionedNavLink,
  Link as VersionedRouterLink,
} from 'components/RouterLink';
import Text from 'components/Text';
import TabContext from 'containers/TabContext';
import { colors, fontWeights } from 'theme';
import { IDEALIST_UTM_PARAMS } from 'utils/constants/general';
import { hasUTMParam, isExternalURL, pathWithParams } from 'utils/url';

const linkStyles = css`
  color: ${colors.waterBlue};
  font-weight: ${fontWeights.semiBold};
  word-wrap: break-word;
  &,
  &:active,
  &:visited {
    text-decoration: none;
  }
  &:hover {
    color: ${colors.niceBlue};
    text-decoration: underline;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Link: any = styled(
  forwardRef(
    (
      {
        href,
        eventName,
        eventProperties,
        rel,
        style,
        target,
        ...props
      }: {
        children?: ReactNode;
        target?: string;
        href?: string;
        rel?: string;
        eventName?: string;
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        eventProperties?: Record<string, any>;
        style?: CSSProperties;
        'data-qa-id'?: string;
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        as?: any;
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tabIndex?: any;
        onClick?: MouseEventHandler;
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        disabled?: any;
        title?: string;
      },
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref: any
    ) => {
      const linkRel = isExternalURL(href) ? 'noopener noreferrer' : rel;
      const linkTarget = isExternalURL(href) ? '_blank' : target;

      return (
        <TabContext.Consumer>
          {(isTabbing /* eslint-disable jsx-a11y/anchor-has-content */) => (
            <a
              ref={ref}
              href={
                isExternalURL(href) && !hasUTMParam(href)
                  ? pathWithParams(href, IDEALIST_UTM_PARAMS)
                  : href
              }
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
              style={isTabbing ? style : { ...style, outline: 0 }}
              rel={linkRel}
              target={linkTarget}
            />
            /* eslint-enable jsx-a11y/anchor-has-content */
          )}
        </TabContext.Consumer>
      );
    }
  )
)<MarginProps & ColorProps>`
  ${linkStyles}
  ${space}
`;
export default Link;
export const RouterLink = styled(VersionedRouterLink)`
  ${linkStyles}
  ${space}
`;
export const RouterNavLink = styled(VersionedNavLink)`
  ${linkStyles}
  ${space}
`;
const StyledChevronUpIcon = styled(ChevronUpIcon)`
  transform: rotate(${(props) => (props.open ? '180deg' : '0')});
  path,
  polyline {
    fill: currentColor;
  }
`;

const withArrow =
  (Component) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/function-component-definition
  (
    {
      children,
      open = false,
      ...props
    }: {
      children: ReactNode;
      open?: boolean;
    } // TODO: Fix this the next time the file is edited.
  ) =>
    (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <Component {...props}>
        {children}
        {/* @ts-expect-error Fix type */}
        <Container as="span" pl="1em">
          <StyledChevronUpIcon open={open} />
        </Container>
      </Component>
    );

export const ArrowLink = withArrow(Link);
export const ArrowRouterLink = withArrow(RouterLink);
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition, @typescript-eslint/no-explicit-any
export const LargeLink = (props: Record<string, any>) => (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/jsx-props-no-spreading
  <RouterLink {...props}>
    <Text
      as="span"
      textAlign={['center', 'center', 'center', 'left']}
      fontSize={['24px', '24px', '24px', '22px']}
    >
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line react/destructuring-assignment */}
      {props.children}
    </Text>
  </RouterLink>
);
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const ConditionalLink = ({
  link,
  children,
  ...props
}: {
  link: string | null | undefined;
  children: ReactNode;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (...args: Array<any>) => any;
  'data-qa-id'?: string;
}) =>
  link ? (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Link data-qa-id={props['data-qa-id']} href={link} {...props}>
      {children}
    </Link>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{children}</>
  );
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const ConditionalRouterLink = ({
  link,
  children,
  component,
  ...props
}: {
  link: string | null | undefined;
  children: ReactNode;
  component?: ComponentType<{
    'data-qa-id'?: string;
    to: string;
  }>;
  'data-qa-id'?: string;
}) => {
  const Component = component || RouterLink;
  return link ? (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Component data-qa-id={props['data-qa-id']} to={link} {...props}>
      {children}
    </Component>
  ) : (
    children
  );
};
